import React, { useEffect, useMemo, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { FaCheckCircle, FaClock, FaDownload, FaExclamationTriangle, FaImages } from 'react-icons/fa';
import { downloadPhoto, getCinetPayPaymentStatus, getImageUrl, getMyPurchases } from '../services/api';
import { useCart } from '../context/CartContext';
import './Checkout.css';

const normalizeStatus = (status) => String(status || 'pending').toLowerCase();

// Re-check an unsettled payment for ~90s (30 x 3s) before giving up.
const POLL_INTERVAL_MS = 3000;
const POLL_ATTEMPTS = 30;

const getPhotoDisplayName = (purchase) => {
  const photo = purchase?.photo || {};
  const name = photo.name || photo.title || photo.filename || `Photo ${purchase?.photo_id || ''}`;
  return String(name).split('/').pop() || name;
};

function CheckoutResult() {
  const [searchParams] = useSearchParams();
  const transaction = searchParams.get('transaction') || '';
  const payment = searchParams.get('payment') || '';
  const [loading, setLoading] = useState(true);
  const [status, setStatus] = useState(payment || 'pending');
  const [purchases, setPurchases] = useState([]);
  const [error, setError] = useState('');
  const [downloadingId, setDownloadingId] = useState(null);
  const { clearCart } = useCart();

  useEffect(() => {
    // CinetPay confirms out of band, so the first lookup usually lands while
    // the payment is still in flight. Keep polling until it settles instead of
    // leaving the customer on a "pending" screen they have to refresh by hand.
    let cancelled = false;
    let timerId = null;

    const isSettled = (value) => ['success', 'completed', 'failed'].includes(normalizeStatus(value));

    const loadResult = async (attempt = 0) => {
      if (cancelled) return;

      if (attempt === 0) {
        setLoading(true);
        setError('');
      }

      try {
        if (transaction) {
          const response = await getCinetPayPaymentStatus(transaction);
          if (cancelled) return;

          const nextPurchases = Array.isArray(response.purchases) ? response.purchases : [];
          const nextStatus = response.payment_status || payment || 'pending';
          setStatus(nextStatus);
          setPurchases(nextPurchases);
          setError('');

          if (nextPurchases.some(item => normalizeStatus(item.payment_status) === 'completed')) {
            clearCart();
          }

          if (!isSettled(nextStatus) && attempt < POLL_ATTEMPTS) {
            timerId = setTimeout(() => loadResult(attempt + 1), POLL_INTERVAL_MS);
          }
        } else {
          const response = await getMyPurchases();
          if (cancelled) return;

          const completed = (response.purchases || []).filter(item => normalizeStatus(item.payment_status) === 'completed');
          setStatus(completed.length > 0 ? 'success' : payment || 'pending');
          setPurchases(completed);
        }
      } catch (err) {
        if (cancelled) return;

        // A failed poll is not necessarily a failed payment; keep retrying and
        // only surface the error once there are no attempts left.
        if (transaction && attempt < POLL_ATTEMPTS) {
          timerId = setTimeout(() => loadResult(attempt + 1), POLL_INTERVAL_MS);
        } else {
          setError(err.message || 'Impossible de vérifier le paiement.');
        }
      } finally {
        if (!cancelled && attempt === 0) {
          setLoading(false);
        }
      }
    };

    loadResult();

    return () => {
      cancelled = true;
      if (timerId) clearTimeout(timerId);
    };
  }, [clearCart, payment, transaction]);

  const completedPurchases = useMemo(
    () => purchases.filter(item => normalizeStatus(item.payment_status) === 'completed'),
    [purchases]
  );
  const displayPurchases = completedPurchases.length > 0 ? completedPurchases : purchases;
  const normalizedStatus = normalizeStatus(status);
  const isSuccess = normalizedStatus === 'success' || normalizedStatus === 'completed';
  const isFailed = normalizedStatus === 'failed';
  const StatusIcon = isSuccess ? FaCheckCircle : isFailed ? FaExclamationTriangle : FaClock;

  const handleDownload = async (purchase) => {
    if (!purchase.photo_id || normalizeStatus(purchase.payment_status) !== 'completed') return;

    setDownloadingId(purchase.id);
    try {
      const blob = await downloadPhoto(purchase.photo_id);
      const url = window.URL.createObjectURL(new Blob([blob]));
      const link = document.createElement('a');
      link.href = url;
      link.download = getPhotoDisplayName(purchase);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch (err) {
      setError(err.message || 'Le téléchargement a échoué.');
    } finally {
      setDownloadingId(null);
    }
  };

  if (loading) {
    return (
      <div className="checkout-page">
        <div className="checkout-loading">
          <div className="spinner"></div>
          <p>Vérification du paiement...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="checkout-page">
      <section className={`checkout-result-header ${isSuccess ? 'success' : isFailed ? 'failed' : 'pending'}`}>
        <StatusIcon />
        <div>
          <p className="checkout-kicker">Retour CinetPay</p>
          <h1>{isSuccess ? 'Paiement confirmé' : isFailed ? 'Paiement échoué' : 'Paiement en vérification'}</h1>
          <p>
            {isSuccess
              ? 'Vos photos achetées sont disponibles ci-dessous.'
              : isFailed
                ? "Le paiement n'a pas été finalisé. Vous pouvez relancer le paiement depuis votre panier ou vos événements."
                : 'La confirmation peut prendre quelques instants. Cette page se met à jour automatiquement.'}
          </p>
          {transaction && <code>{transaction}</code>}
        </div>
      </section>

      {error && <div className="error-message checkout-error">{error}</div>}

      {displayPurchases.length === 0 ? (
        <section className="checkout-empty checkout-result-empty">
          <FaImages />
          <p>Aucune photo trouvée pour cette transaction.</p>
          <Link to="/payment-history">Voir mon historique</Link>
        </section>
      ) : (
        <section className="checkout-purchased-grid">
          {displayPurchases.map(purchase => {
            const purchaseStatus = normalizeStatus(purchase.payment_status);
            const photo = purchase.photo || {};
            const imageUrl = getImageUrl(photo.filename || photo.url);

            return (
              <article key={purchase.id} className="checkout-purchased-card">
                {imageUrl ? (
                  <img src={imageUrl} alt={getPhotoDisplayName(purchase)} />
                ) : (
                  <div className="checkout-photo-placeholder"><FaImages /></div>
                )}
                <div>
                  <strong>{getPhotoDisplayName(purchase)}</strong>
                  <span>{photo.event?.name || 'Événement'}</span>
                  <span>{Number(purchase.amount || 0).toLocaleString('fr-FR', { maximumFractionDigits: 0 })} F</span>
                </div>
                <button
                  type="button"
                  disabled={purchaseStatus !== 'completed' || downloadingId === purchase.id}
                  onClick={() => handleDownload(purchase)}
                >
                  <FaDownload />
                  <span>{downloadingId === purchase.id ? 'Téléchargement...' : 'Télécharger'}</span>
                </button>
              </article>
            );
          })}
        </section>
      )}
    </div>
  );
}

export default CheckoutResult;
